import { rememberedSecret } from "@/app/relogin";
import { useSession } from "@/app/session";
import { declinePinOffer, resetPinOffer, useVault } from "@/app/vault";
import { PinCreate } from "@/screens/unlock/PinCreate";
import { PIN_HONESTY } from "@/screens/unlock/PinSetupScreen";
import { PinVerify } from "@/screens/unlock/PinVerify";
import { type ReloginSecret, forgetSecret } from "@/unlock";
import { useState } from "react";

type Mode = "idle" | "create" | "verify" | "confirm-remove";

export function PinSection() {
  const session = useSession();
  const { ownerKey, status, refresh } = useVault();
  const [mode, setMode] = useState<Mode>("idle");
  const [secret, setSecret] = useState<ReloginSecret | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const label = session.user.display_name ?? session.user.email ?? "Tu cuenta";

  function back() {
    setMode("idle");
    setSecret(null);
    refresh();
  }

  function startCreate() {
    const known = rememberedSecret();
    if (!known) {
      resetPinOffer(ownerKey);
      setNote(
        "Para guardar el PIN necesitamos tu contraseña, y en esta sesión no la tenemos. Te lo vamos a ofrecer la próxima vez que entres escribiéndola.",
      );
      return;
    }
    setNote(null);
    setSecret(known);
    setMode("create");
  }

  return (
    <section className="lead-group">
      <h2 className="t-eyebrow">Entrar con PIN</h2>

      {mode === "idle" ? (
        <>
          <ul className="lead-rows">
            <li className="lead-rows__item">
              <div className="lead-row lead-row--static">
                <span className="lead-row__body">
                  <span className="profile-row__title">
                    {status.saved ? "PIN puesto" : "Sin PIN"}
                  </span>
                  <span className="profile-row__text">
                    {status.saved
                      ? "Cuando la sesión vence, entrás con seis dígitos."
                      : "Cuando la sesión vence, escribís usuario y contraseña."}
                  </span>
                </span>
              </div>
            </li>
            {status.saved ? (
              <>
                <li className="lead-rows__item">
                  <button
                    type="button"
                    className="lead-row lead-row--button profile-action"
                    onClick={() => {
                      setNote(null);
                      setMode("verify");
                    }}
                  >
                    Cambiar el PIN
                  </button>
                </li>
                <li className="lead-rows__item">
                  <button
                    type="button"
                    className="lead-row lead-row--button profile-action profile-action--danger"
                    onClick={() => {
                      setNote(null);
                      setMode("confirm-remove");
                    }}
                  >
                    Quitar el PIN
                  </button>
                </li>
              </>
            ) : (
              <li className="lead-rows__item">
                <button
                  type="button"
                  className="lead-row lead-row--button profile-action"
                  onClick={startCreate}
                >
                  Poner un PIN
                </button>
              </li>
            )}
          </ul>
        </>
      ) : null}

      <output className={note && mode === "idle" ? "notice notice--info" : undefined}>
        {mode === "idle" ? note : null}
      </output>

      {mode === "idle" ? <p className="profile__note">{PIN_HONESTY}</p> : null}

      {mode === "verify" ? (
        <PinVerify
          ownerKey={ownerKey}
          title="Marcá tu PIN actual"
          hint="Lo pedimos para asegurarnos de que el teléfono es tuyo. Esto funciona sin señal."
          onVerified={(known) => {
            setSecret(known);
            setMode("create");
          }}
          onWiped={() => {
            setNote(
              "Se agotaron los intentos y borramos la contraseña guardada. Podés poner un PIN nuevo la próxima vez que entres con tu contraseña.",
            );
            back();
          }}
          onMissing={back}
          footer={
            <button type="button" className="btn btn--block" onClick={back}>
              Cancelar
            </button>
          }
        />
      ) : null}

      {mode === "create" && secret ? (
        <PinCreate
          ownerKey={ownerKey}
          secret={secret}
          label={label}
          onDone={back}
          onCancel={back}
        />
      ) : null}

      {mode === "confirm-remove" ? (
        <>
          <p className="profile__note">
            Se borra la contraseña guardada en este teléfono. Vas a seguir adentro, pero cuando la
            sesión venza vas a entrar escribiendo usuario y contraseña.
          </p>
          <div className="profile__confirm">
            <button
              type="button"
              className="btn btn--primary btn--block"
              onClick={() => {
                declinePinOffer(ownerKey);
                void forgetSecret().finally(back);
              }}
            >
              Quitar el PIN
            </button>
            <button type="button" className="btn btn--block" onClick={back}>
              Dejarlo como está
            </button>
          </div>
        </>
      ) : null}
    </section>
  );
}
